import { useAuth } from "@/contexts/AuthContext";
import { submitSurvey } from "@/services/generatedAPIs";
import { SurveyItem, UserInfoResponseRole } from "@/services/generatedAPIs.schemas";
import { useEffect, useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { toast } from "sonner";

type Lang = "zh" | "en";

const content = {
    zh: {
        switchLabel: "English",
        title: "教師版EASE平台使用體驗問卷",
        intro: "感謝閣下使用 EASE 平台！為了進一步提升平台的使用體驗，我們誠摯邀請閣下分享真實的使用體驗。",
        part1Title: "第一部分：平台使用體驗與教學效能",
        freqQ: "我現時使用EASE 平台的頻次大概是：",
        freqA: "A. 一個月少於一次",
        freqB: "B. 一個月一次",
        freqC: "C. 兩星期一次",
        freqD: "D. 一星期一次",
        freqE: "E. 一星期多於一次",
        part1Desc: "請根據閣下使用 EASE 平台的真實體驗，針對以下描述選擇最符合的選項（1：非常不同意，2：不同意，3：一般，4：同意，5：非常同意）：",
        likert1: "1 非常不同意",
        likert2: "2 不同意",
        likert3: "3 一般",
        likert4: "4 同意",
        likert5: "5 非常同意",
        q1: "1. 使用 EASE 平台提升了我的工作效率。",
        q2: "2. 使用 EASE 平台減輕了我的工作負擔。",
        q3: "3. 我認為 EASE 平台容易使用。",
        q4: "4. 使用 EASE 平台，我可以輕易達成我想做的事情。",
        q5: "5. 使用 EASE 平台幫助我更有效地評估學生的學習進度。",
        q6: "6. EASE平台內的遊戲化功能（例如：積分系統）提升了學生使用平台的興趣。",
        q7: "7. 學會使用 EASE 平台花費了我很多時間。",
        q8: "8. 日常使用 EASE 平台佔用了我太多時間。",
        part2Title: "第二部分：數據應用與平台功能",
        q9: "EASE 平台提供的哪些數據對閣下的教學最有幫助（可多選）？",
        q9a: "a) 總覽 - 作業完成情況",
        q9b: "b) 總覽 - 課業狀況總覽",
        q9c: "c) 總覽 - 錯誤情況",
        q9d: "d) 總覽 - 智能總結",
        q9e: "e) 課業狀況 - 查看學生功課完成情況",
        q9f: "f) 課業狀況 - 查看學生隨心練完成情況",
        q9g: "g) 課業狀況 - 學生求助",
        q9h: "h) 課業狀況 - 批改作業",
        q9i: "i) 題庫管理 - AI 題目生成",
        q9j: "j) 分派課業 – 系統組題",
        q9k: "k) 分派課業 - AI 題目生成",
        q9l: "l) 其他，請列出：",
        q9lPlaceholder: "請列出",
        q10: "為了讓 EASE平台更有效地輔助教師的教學與學生的學習，閣下對平台有什麼建議（開放式問題）？",
        part3Title: "第三部分：未來使用意願與支援需求",
        q11: "我將來會繼續使用 EASE 平台。",
        futureFreqQ: "使用的頻次將會是：",
        futureFreqA: "A. 一個月一次",
        futureFreqB: "B. 兩星期一次",
        futureFreqC: "C. 一星期一次",
        futureFreqD: "D. 一星期多於一次",
        q13: "哪些支援措施能幫助閣下更好地使用 EASE 平台（可多選）？",
        q13a: "a) 開設EASE平台專屬支援熱線電話",
        q13b: "b) 提供更多到校支援服務",
        q13c: "c) 其他，請列明：",
        q13cPlaceholder: "請列明",
        q14: "總體來說，我喜歡這個平台。",
        q14_1: "請簡單說明原因：",
        submit: "提交問卷",
        thankYou: "感謝閣下的參與！",
    },
    en: {
        switchLabel: "中文",
        title: "EASE Platform User Experience Survey (Teacher Version)",
        intro: "Thank you for using the EASE platform! To further enhance the user experience on the platform, we sincerely invite you to share your experience.",
        part1Title: "Part 1: Platform User Experience and Teaching Effectiveness",
        freqQ: "1. My current frequency of using the EASE platform is approximately:",
        freqA: "Less than once a month",
        freqB: "Once a month",
        freqC: "Once every two weeks",
        freqD: "Once a week",
        freqE: "More than once a week",
        part1Desc: "Based on your experience using the EASE platform, please select the option that best fits the following statements (1: Strongly Disagree, 2: Disagree, 3: Neutral, 4: Agree, 5: Strongly Agree):",
        likert1: "1 Strongly Disagree",
        likert2: "2 Disagree",
        likert3: "3 Neutral",
        likert4: "4 Agree",
        likert5: "5 Strongly Agree",
        q1: "2. Using the EASE platform has improved my work efficiency.",
        q2: "3. Using the EASE platform has reduced my workload.",
        q3: "4. I find the EASE platform easy to use.",
        q4: "5. Using the EASE platform, I can easily accomplish what I want to do.",
        q5: "6. Using the EASE platform helps me evaluate students' learning progress more effectively.",
        q6: "7. The gamification features on the EASE platform (e.g., the points system) have increased my students' interest in using the platform.",
        q7: "8. Learning to use the EASE platform took me a lot of time.",
        q8: "9. Daily use of the EASE platform takes up too much of my time.",
        part2Title: "Part 2: Data Application and Platform Features",
        q9: "10. Which data provided by the EASE platform are most helpful for your teaching? (Multiple choices allowed)",
        q9a: "Overview - Homework Completion Status",
        q9b: "Overview - Homework Correctness Overview",
        q9c: "Overview - Mistake Details",
        q9d: "Overview - Intelligent Summary",
        q9e: "Homework Status - View Student Homework Completion",
        q9f: "Homework Status - View Student Free Practice Completion",
        q9g: "Homework Status - Request(s) from Student(s)",
        q9h: "Homework Status - Homework Review",
        q9i: "Question Bank - AI Question Generation",
        q9j: "Assign Homework – Set by System",
        q9k: "Assign Homework - AI Select Topics",
        q9l: "Other, please specify:",
        q9lPlaceholder: "Please specify",
        q10: "11. To allow the EASE platform to assist teachers in teaching and students in learning more effectively, what would you suggest for the platform? (Open-ended question)",
        part3Title: "Part 3: Future Usage Intentions and Support Needs",
        q11: "12. I will continue to use the EASE platform in the future.",
        futureFreqQ: "13. The frequency of use will be:",
        futureFreqA: "Once a month",
        futureFreqB: "Once every two weeks",
        futureFreqC: "Once a week",
        futureFreqD: "More than once a week",
        q13: "14. What support measures would help you use the EASE platform better? (Multiple choices allowed)",
        q13a: "Set up a telephone hotline for the EASE platform",
        q13b: "Provide more on-site school support services",
        q13c: "Other, please specify:",
        q13cPlaceholder: "Please specify",
        q14: "15. Overall, I like this platform (1: Strongly Disagree, 2: Disagree, 3: Neutral, 4: Agree, 5: Strongly Agree).",
        q14_1: "16. Please briefly explain the reason (Open-ended question).",
        submit: "Submit Survey",
        thankYou: "Thank you for your participation!",
    },
} as const;

function Survey6({
    t,
    onSuccess,
}: WithTranslation & { onSuccess?: () => void }) {
    const { getCurrentUser } = useAuth();
    const [lang, setLang] = useState<Lang>("zh");
    const c = content[lang];

    const freqOptions = [
        { value: "A", text: c.freqA },
        { value: "B", text: c.freqB },
        { value: "C", text: c.freqC },
        { value: "D", text: c.freqD },
        { value: "E", text: c.freqE },
    ];

    const likertQuestions = [
        { id: 2, text: c.q1 },
        { id: 3, text: c.q2 },
        { id: 4, text: c.q3 },
        { id: 5, text: c.q4 },
        { id: 6, text: c.q5 },
        { id: 7, text: c.q6 },
        { id: 8, text: c.q7 },
        { id: 9, text: c.q8 },
    ];

    const dataOptions = [
        { value: "a", text: c.q9a },
        { value: "b", text: c.q9b },
        { value: "c", text: c.q9c },
        { value: "d", text: c.q9d },
        { value: "e", text: c.q9e },
        { value: "f", text: c.q9f },
        { value: "g", text: c.q9g },
        { value: "h", text: c.q9h },
        { value: "i", text: c.q9i },
        { value: "j", text: c.q9j },
        { value: "k", text: c.q9k },
        { value: "l", text: c.q9l },
    ];

    const futureFreqOptions = [
        { value: "A", text: c.futureFreqA },
        { value: "B", text: c.futureFreqB },
        { value: "C", text: c.futureFreqC },
        { value: "D", text: c.futureFreqD },
    ];

    const supportOptions = [
        { value: "a", text: c.q13a },
        { value: "b", text: c.q13b },
        { value: "c", text: c.q13c },
    ];

    const [freq, setFreq] = useState("");
    const [likertAnswers, setLikertAnswers] = useState<Record<number, string>>({});
    const [q9Selected, setQ9Selected] = useState<string[]>([]);
    const [q9Other, setQ9Other] = useState("");
    const [q10, setQ10] = useState("");
    const [futureFreq, setFutureFreq] = useState("");
    const [q13Selected, setQ13Selected] = useState<string[]>([]);
    const [q13Other, setQ13Other] = useState("");
    const [q14_1, setQ14_1] = useState("");
    const [answerTimes, setAnswerTimes] = useState<Record<number, string>>({});
    const [startTime, setStartTime] = useState<Date>();

    useEffect(() => {
        setStartTime(new Date());
    }, []);

    const handleQ9Toggle = (value: string) => {
        setQ9Selected(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
    };

    const handleQ13Toggle = (value: string) => {
        setQ13Selected(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const items: SurveyItem[] = [];

        if (!freq) {
            toast.error(t('component.needanswer'));
            return;
        }
        items.push({ questionId: 1, answer: freq, answerAt: answerTimes[1]! });

        for (const q of likertQuestions) {
            const answer = likertAnswers[q.id];
            if (!answer) {
                toast.error(t('component.needanswer'));
                return;
            }
            items.push({ questionId: q.id, answer, answerAt: answerTimes[q.id]! });
        }

        if (q9Selected.length === 0) {
            toast.error(t('component.needanswer'));
            return;
        }
        const q9Answer = q9Selected.map(v => v === "l" ? `l) ${q9Other}` : v).join(",");
        items.push({ questionId: 10, answer: q9Answer, answerAt: answerTimes[10]! });

        items.push({ questionId: 11, answer: q10, answerAt: answerTimes[11] ?? new Date().toISOString() });

        const q12Answer = likertAnswers[12];
        if (!q12Answer) {
            toast.error(t('component.needanswer'));
            return;
        }
        items.push({ questionId: 12, answer: q12Answer, answerAt: answerTimes[12]! });

        if (!futureFreq) {
            toast.error(t('component.needanswer'));
            return;
        }
        items.push({ questionId: 13, answer: futureFreq, answerAt: answerTimes[13]! });

        if (q13Selected.length === 0) {
            toast.error(t('component.needanswer'));
            return;
        }
        const q14Answer = q13Selected.map(v => v === "c" ? `c) ${q13Other}` : v).join(",");
        items.push({ questionId: 14, answer: q14Answer, answerAt: answerTimes[14]! });

        const q15Answer = likertAnswers[15];
        if (!q15Answer) {
            toast.error(t('component.needanswer'));
            return;
        }
        items.push({ questionId: 15, answer: q15Answer, answerAt: answerTimes[15]! });

        items.push({ questionId: 16, answer: q14_1, answerAt: answerTimes[16] ?? new Date().toISOString() });

        submitSurvey({
            userId: getCurrentUser()!.id,
            userType: getCurrentUser()!.role === UserInfoResponseRole.STUDENT ? 1 : 2,
            surveyId: 6,
            startTime: startTime!.toISOString(),
            submitTime: new Date().toISOString(),
            items,
        }).then(rsp => {
            if (rsp.code === 200 && onSuccess) {
                onSuccess();
            }
        }).catch(() => {});

        return false;
    };

    const likertColors = [
        "border-2 border-[#e76f51] peer-checked:bg-[#e76f51] peer-checked:text-white",
        "border-2 border-[#f4a261] peer-checked:bg-[#f4a261] peer-checked:text-white",
        "border-2 border-[#e9c46a] peer-checked:bg-[#e9c46a] peer-checked:text-white",
        "border-2 border-[#95d5b2] peer-checked:bg-[#95d5b2] peer-checked:text-white",
        "border-2 border-[#2a9d8f] peer-checked:bg-[#2a9d8f] peer-checked:text-white",
    ];

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
            <div className="bg-white/80 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-xl w-full max-w-3xl border border-white/50 relative max-h-[90vh] overflow-y-auto">
                {/* Language toggle */}
                <button
                    type="button"
                    className="absolute top-4 right-4 text-sm text-blue-600 hover:text-blue-800 underline"
                    onClick={() => setLang(lang === "zh" ? "en" : "zh")}
                >
                    {c.switchLabel}
                </button>

                <h2 className="text-2xl font-bold text-slate-700 text-center mb-2">{c.title}</h2>
                <p className="text-sm text-slate-600 text-center mb-4">{c.intro}</p>

                <form className="space-y-6" onSubmit={handleSubmit}>
                    {/* Part 1: Platform Experience & Teaching Effectiveness */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-bold text-slate-700 mb-4">{c.part1Title}</h3>

                        {/* Current frequency */}
                        <div className="mb-6">
                            <label className="block font-medium text-m text-black mb-2">
                                {c.freqQ} <span className="text-red-500">*</span>
                            </label>
                            <div className="space-y-2 ml-4">
                                {freqOptions.map(opt => (
                                    <label key={opt.value} className="flex items-center">
                                        <input
                                            type="radio"
                                            name="freq"
                                            value={opt.value}
                                            className="mr-2"
                                            required
                                            onChange={(e) => {
                                                setFreq(e.target.value);
                                                setAnswerTimes(prev => ({ ...prev, [1]: new Date().toISOString() }));
                                            }}
                                        />
                                        <span className="text-sm">{opt.text}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Likert questions */}
                        <p className="text-sm text-slate-600 mb-4">{c.part1Desc}</p>

                        <div className="flex gap-4 justify-center mb-4 text-sm text-slate-600">
                            <span>{c.likert1}</span>
                            <span>{c.likert2}</span>
                            <span>{c.likert3}</span>
                            <span>{c.likert4}</span>
                            <span>{c.likert5}</span>
                        </div>

                        {likertQuestions.map((q) => (
                            <div key={q.id} className="mb-4 pb-4 border-b border-slate-100 last:border-b-0">
                                <label className="block font-medium text-m text-black mb-2">
                                    {q.text} <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-6 justify-center">
                                    {[1, 2, 3, 4, 5].map((val, optionIndex) => (
                                        <label key={val} className="flex items-center mr-2">
                                            <input
                                                type="radio"
                                                name={`q${q.id}`}
                                                value={val}
                                                className="peer hidden"
                                                required
                                                onChange={(e) => {
                                                    setLikertAnswers(prev => ({ ...prev, [q.id]: e.target.value }));
                                                    setAnswerTimes(prev => ({ ...prev, [q.id]: new Date().toISOString() }));
                                                }}
                                            />
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center border transition-transform hover:scale-105 ${likertColors[optionIndex]}
                                                peer-checked:[&>svg]:opacity-100 peer-checked:[&>svg]:scale-100`}>
                                                <svg className="w-8 h-8 opacity-0 scale-95 transition-transform" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Part 2: Data Application & Platform Features */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-bold text-slate-700 mb-4">{c.part2Title}</h3>

                        {/* Multi-select: helpful data */}
                        <div className="mb-6">
                            <label className="block font-medium text-m text-black mb-2">
                                {c.q9} <span className="text-red-500">*</span>
                            </label>
                            <div className="space-y-2 ml-4">
                                {dataOptions.map(opt => (
                                    <label key={opt.value} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            value={opt.value}
                                            className="mr-2"
                                            onChange={() => {
                                            handleQ9Toggle(opt.value);
                                            setAnswerTimes(prev => ({ ...prev, [10]: new Date().toISOString() }));
                                        }}
                                        />
                                        <span className="text-sm">{opt.text}</span>
                                        {opt.value === "l" && (
                                            <input
                                                type="text"
                                                className="ml-2 border rounded px-2 py-1 text-sm flex-1"
                                                placeholder={c.q9lPlaceholder}
                                                value={q9Other}
                                                onChange={(e) => {
                                                    setQ9Other(e.target.value);
                                                    setAnswerTimes(prev => ({ ...prev, [10]: new Date().toISOString() }));
                                                }}
                                            />
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Open-ended: suggestions */}
                        <div>
                            <label className="block font-medium text-m text-black mb-2">{c.q10}</label>
                            <textarea
                                className="w-full border rounded-lg p-2 text-sm"
                                rows={3}
                                value={q10}
                                onChange={(e) => {
                                    setQ10(e.target.value);
                                    setAnswerTimes(prev => ({ ...prev, [11]: new Date().toISOString() }));
                                }}
                            />
                        </div>
                    </div>

                    {/* Part 3: Future Usage & Support Needs */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-bold text-slate-700 mb-2">{c.part3Title}</h3>

                        {/* Likert: will continue to use */}
                        <div className="mb-6 pb-4 border-b border-slate-100">
                            <label className="block font-medium text-m text-black mb-2">
                                {c.q11} <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-4 justify-center mb-2 text-sm text-slate-600">
                                <span>{c.likert1}</span>
                                <span>{c.likert2}</span>
                                <span>{c.likert3}</span>
                                <span>{c.likert4}</span>
                                <span>{c.likert5}</span>
                            </div>
                            <div className="flex gap-6 justify-center">
                                {[1, 2, 3, 4, 5].map((val, optionIndex) => (
                                    <label key={val} className="flex items-center mr-2">
                                        <input
                                            type="radio"
                                            name="q12"
                                            value={val}
                                            className="peer hidden"
                                            required
                                            onChange={(e) => {
                                                setLikertAnswers(prev => ({ ...prev, [12]: e.target.value }));
                                                setAnswerTimes(prev => ({ ...prev, [12]: new Date().toISOString() }));
                                            }}
                                        />
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border transition-transform hover:scale-105 ${likertColors[optionIndex]}
                                            peer-checked:[&>svg]:opacity-100 peer-checked:[&>svg]:scale-100`}>
                                            <svg className="w-8 h-8 opacity-0 scale-95 transition-transform" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Future frequency */}
                        <div className="mb-6">
                            <label className="block font-medium text-m text-black mb-2">
                                {c.futureFreqQ} <span className="text-red-500">*</span>
                            </label>
                            <div className="space-y-2 ml-4">
                                {futureFreqOptions.map(opt => (
                                    <label key={opt.value} className="flex items-center">
                                        <input
                                            type="radio"
                                            name="futureFreq"
                                            value={opt.value}
                                            className="mr-2"
                                            required
                                            onChange={(e) => {
                                                setFutureFreq(e.target.value);
                                                setAnswerTimes(prev => ({ ...prev, [13]: new Date().toISOString() }));
                                            }}
                                        />
                                        <span className="text-sm">{opt.text}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Multi-select: support measures */}
                        <div className="mb-6">
                            <label className="block font-medium text-m text-black mb-2">
                                {c.q13} <span className="text-red-500">*</span>
                            </label>
                            <div className="space-y-2 ml-4">
                                {supportOptions.map(opt => (
                                    <label key={opt.value} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            value={opt.value}
                                            className="mr-2"
                                            onChange={() => {
                                            handleQ13Toggle(opt.value);
                                            setAnswerTimes(prev => ({ ...prev, [14]: new Date().toISOString() }));
                                        }}
                                        />
                                        <span className="text-sm">{opt.text}</span>
                                        {opt.value === "c" && (
                                            <input
                                                type="text"
                                                className="ml-2 border rounded px-2 py-1 text-sm flex-1"
                                                placeholder={c.q13cPlaceholder}
                                                value={q13Other}
                                                onChange={(e) => {
                                                    setQ13Other(e.target.value);
                                                    setAnswerTimes(prev => ({ ...prev, [14]: new Date().toISOString() }));
                                                }}
                                            />
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Likert: overall like */}
                        <div className="mb-4 pb-4 border-b border-slate-100">
                            <label className="block font-medium text-m text-black mb-2">
                                {c.q14} <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-4 justify-center mb-2 text-sm text-slate-600">
                                <span>{c.likert1}</span>
                                <span>{c.likert2}</span>
                                <span>{c.likert3}</span>
                                <span>{c.likert4}</span>
                                <span>{c.likert5}</span>
                            </div>
                            <div className="flex gap-6 justify-center">
                                {[1, 2, 3, 4, 5].map((val, optionIndex) => (
                                    <label key={val} className="flex items-center mr-2">
                                        <input
                                            type="radio"
                                            name="q15"
                                            value={val}
                                            className="peer hidden"
                                            required
                                            onChange={(e) => {
                                                setLikertAnswers(prev => ({ ...prev, [15]: e.target.value }));
                                                setAnswerTimes(prev => ({ ...prev, [15]: new Date().toISOString() }));
                                            }}
                                        />
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border transition-transform hover:scale-105 ${likertColors[optionIndex]}
                                            peer-checked:[&>svg]:opacity-100 peer-checked:[&>svg]:scale-100`}>
                                            <svg className="w-8 h-8 opacity-0 scale-95 transition-transform" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Open-ended: reason */}
                        <div>
                            <label className="block font-medium text-m text-black mb-2">{c.q14_1}</label>
                            <textarea
                                className="w-full border rounded-lg p-2 text-sm"
                                rows={3}
                                value={q14_1}
                                onChange={(e) => {
                                    setQ14_1(e.target.value);
                                    setAnswerTimes(prev => ({ ...prev, [16]: new Date().toISOString() }));
                                }}
                            />
                        </div>
                    </div>

                    {/* Thank you */}
                    <p className="text-center text-sm text-slate-600">{c.thankYou}</p>

                    <div className="text-center mt-4">
                        <button type="submit" className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
                            {c.submit}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default withTranslation()(Survey6);
